import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Container, Eyebrow, Section, ArrowLink, CtaBand, AnchorPills } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { EventRow } from "@/components/shared/EventCard";
import { Seo } from "@/components/shared/Seo";
import { TicketStory } from "@/components/arranger/TicketStory";
import { FeeCalculator } from "@/components/arranger/FeeCalculator";
import { FreeDates } from "@/components/arranger/FreeDates";
import { OrganizerLinkRequest } from "@/components/arranger/OrganizerLinkRequest";
import { fetchArrangerInfo, fetchEvents, fetchRooms, fetchSite } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Img, SIZES } from "@/components/shared/Img";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";

const isOwnPhoto = (src) => typeof src === "string" && src.startsWith("/");
const roomImg = (rooms, slug, i = 0) => (rooms || []).find((r) => r.slug === slug)?.images?.[i];
const roomBy = (rooms, id) => (rooms || []).find((r) => r.id === id);

/* Starting points – each one lands in the request form with room + type already chosen. */
const FORMATS = [
  { id: "konsert", title: "Konsert eller framsyning", text: "Scene, amfi og godt lydanlegg. Billettar med Vipps rett til deg.", room: "king-oscar-amfi", type: "Konsert", img: 0 },
  { id: "intim", title: "Quiz, pubkveld eller intimkonsert", text: "Kafeen med bar og nærleik. Perfekt for faste kveldar.", room: "kippers-kafe", type: "Quiz", img: 0 },
  { id: "foredrag", title: "Foredrag, kurs eller debatt", text: "Stovene tek 110 i rolege omgivnader – eller Mussa for dei mindre.", room: "stovene", type: "Foredrag / debatt", img: 0 },
  { id: "festival", title: "Festival eller marknad", text: "Heile huset, alle etasjar. Vi planlegg saman med deg.", room: "heile-huset", type: "Festival / marknad", img: 0, fallback: "king-oscar-amfi" },
];

const STEPS = [
  ["Send førespurnad", "Fortel kort kva du vil lage, kva dato og kva rom. Fem minutt – og ikkje bindande."],
  ["Styret stadfestar", "Vi sjekkar kalenderen og svarar på e-post, vanlegvis i løpet av nokre dagar."],
  ["Vi set opp", "Arrangementet kjem i programmet – med billettar om du vil. Du får ei personleg arrangørlenke på e-post."],
  ["Du arrangerer", "Følg salet, registrer betalingar og slepp inn folk med QR frå mobilen. Pengane går rett til deg."],
];

const INCLUDED = [
  "Personleg arrangørside – ingen passord, lenka kjem på e-post",
  "Oversikt over bestillingar, betalingar og venteliste – oppdaterer seg sjølv",
  "Reservasjon med Vipps-instruks til ditt nummer, eller betaling i døra",
  "QR-billett på mobilen til kvar deltakar, med kalenderfil",
  "Innsjekk med kamera i døra – mørk dørmodus som fungerer i låglys",
  "Automatiske påminningar på e-post, og SMS når huset har det på",
  "Går betalingsfristen ut, får du ei kontrolliste – plassen vert frigjeven først når du har sjekka Vipps",
  "CSV-eksport og ferdig oppgjersgrunnlag etterpå",
];

const FAQ = [
  ["Får eg eigen innlogging?", "Du får ei personleg lenke på e-post – den er innlogginga di. Opna ho på mobilen, så er du inne på arrangørsida med alle arrangementa dine. Mistar du lenka, kan du få ho på nytt nedst på denne sida."],
  ["Må eg bruke billettløysinga til kulturhuset?", "Nei. Du kan arrangere utan billettar, bruke rein påmelding, eller ha di eiga løysing – då lenkar vi til ho frå programmet."],
  ["Kven får pengane frå billettsalet?", "Du. Publikum betaler med Vipps rett til deg, eller kontant/kort i døra. Kulturhuset tek aldri imot pengane, men fakturerer eventuell avgift i etterkant ut frå det stadfesta salet."],
  ["Kva skjer om eg må avlyse eller flytte?", "Ta kontakt med styret, så tek vi arrangementet ut av programmet. Dei som har betalt, refunderer du sjølv – avtalen om billetten er mellom deg og publikum."],
  ["Kan eg få hjelp med lyd, lys og rigg?", "Skriv kva du treng i førespurnaden, så avklarar vi kva huset kan stille med og kva du må ordne sjølv. Teknisk rigging av lys og lyd har eigen timepris i prislista."],
  ["Kor fort får eg svar?", "Styret arbeider på dugnad og svarar vanlegvis i løpet av nokre dagar. Hastar det? Send e-post til post@halsnoykulturhus.no."],
];

const ANCHORS = [["#format", "Vel format"], ["#slik", "Slik fungerer det"], ["#billettar", "Billettane"], ["#pris", "Kva kostar det"], ["#datoar", "Ledige datoar"], ["#sporsmal", "Spørsmål"]];

const TIER_TEXT = { regular: "Lag og foreiningar som brukar huset fast", external: "Alle andre arrangørar" };

const FeeRow = ({ tier }) => (
  <li className="grid grid-cols-[1fr_auto] items-baseline gap-4 border-b border-border py-4 last:border-b-0" data-testid={`arranger-fee-${tier.category}`}>
    <div>
      <div className="text-[16px] font-medium">{tier.label}</div>
      <div className="mt-0.5 text-[13px] text-muted-foreground">{TIER_TEXT[tier.category]}</div>
    </div>
    <div className={cn("font-display text-[22px] font-semibold tracking-[-0.02em] tabular sm:text-[26px]", tier.model === "none" && "text-muted-foreground")}>{tier.fee_text}</div>
  </li>
);

const FormatTile = ({ f, rooms, index }) => {
  const room = roomBy(rooms, f.room);
  const src = [room?.images?.[f.img], ...(room?.images || []), f.fallback && roomImg(rooms, f.fallback)].find(isOwnPhoto);
  const to = `/arranger/soknad?rom=${f.room}&type=${encodeURIComponent(f.type)}`;
  return (
    <FadeUp delay={index * 0.06}>
      <Link to={to} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-4 rounded-[14px]" data-testid={`arranger-format-${f.id}`}>
        <figure className="img-hover relative aspect-[4/3] overflow-hidden rounded-[12px] bg-secondary">
          {isOwnPhoto(src) ? <Img src={src} alt={room?.name || ""} width={800} sizes={SIZES.third} className="absolute inset-0 h-full w-full object-cover" /> : <Skeleton className="absolute inset-0 rounded-none" />}
          <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-ink/70 to-transparent px-4 pb-3 pt-14 text-paper">
            <span className="text-[13px] font-medium">{room?.name}</span>
            {room?.capacity && <span className="t-mono text-paper/80">{room.capacity} pers.</span>}
          </figcaption>
        </figure>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-display text-[20px] font-semibold leading-tight tracking-[-0.015em] text-balance u-hover">{f.title}</h3>
            <p className="mt-1.5 text-[14px] leading-relaxed text-muted-foreground">{f.text}</p>
          </div>
          <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary transition-colors group-hover:bg-foreground group-hover:text-background"><ArrowUpRight className="h-4 w-4" /></span>
        </div>
      </Link>
    </FadeUp>
  );
};

export default function Arranger() {
  const { data: info, isLoading } = useQuery({ queryKey: ["arranger-info"], queryFn: fetchArrangerInfo });
  const { data: rooms } = useQuery({ queryKey: ["rooms"], queryFn: fetchRooms });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const { data: events } = useQuery({ queryKey: ["events-upcoming-3"], queryFn: () => fetchEvents({ limit: 3 }) });

  const stage = info?.stage_room;
  const heroImg = roomImg(rooms, "king-oscar-amfi", 1) || roomImg(rooms, "king-oscar-amfi", 0);
  const sideImg = roomImg(rooms, "king-oscar-amfi", 4) || roomImg(rooms, "kippers-kafe", 2) || roomImg(rooms, "kippers-kafe", 0);

  return (
    <div className="pb-24 lg:pb-0" data-testid="arranger-page">
      <Seo
        title="Arranger noko"
        description="Konsert, teater, quiz, foredrag eller festival på Halsnøy Kulturhus. Scene for 187, kafé, billettløysing med Vipps og QR-innsjekk – og pengane går rett til deg. Send ein førespurnad."
        image={heroImg}
        imageAlt="Fullt hus i King Oscar Amfi"
        path="/arranger"
      />

      {/* HERO ------------------------------------------------------- */}
      <section>
        <Container className="pb-8 pt-10 sm:pt-16 lg:pt-20">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10">
            <div className="lg:col-span-8">
              <Eyebrow>For arrangørar</Eyebrow>
              <h1 className="t-h1 mt-4 max-w-[14ch] text-balance" data-testid="page-title">Du har ideen. Vi har <em className="t-swash">huset</em>.</h1>
            </div>
            <div className="flex flex-col gap-6 lg:col-span-4 lg:pb-3">
              <p className="t-lead max-w-md text-muted-foreground text-pretty">Konsert, teater, quiz, foredrag eller festival. Scene for {stage?.capacity || 187}, kafé, og ei billettløysing der pengane går rett til deg.</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="arranger-hero-cta"><Link to="/arranger/soknad">Send arrangørførespurnad <ArrowUpRight className="h-4 w-4" /></Link></Button>
                <Button asChild variant="outline" size="lg" data-testid="arranger-hero-demo"><a href="#billettar">Sjå korleis det verkar</a></Button>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-3 sm:mt-12 lg:mt-14 lg:grid-cols-12" data-testid="arranger-hero-images">
            <figure className="img-hover relative aspect-[3/2] overflow-hidden rounded-[12px] bg-secondary lg:col-span-8 lg:aspect-[16/9]">
              {heroImg ? <Img src={heroImg} alt="Fullt hus i King Oscar Amfi" width={1600} sizes={SIZES.twoThirds} priority className="absolute inset-0 h-full w-full object-cover" /> : <Skeleton className="absolute inset-0 rounded-none" />}
              {heroImg && <PhotoNumberTag src={heroImg} interactive className="absolute right-2.5 top-2.5" testId="arranger-hero-photo-number" />}
              {stage && (
                <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-ink/75 to-transparent px-5 pb-4 pt-16 text-paper sm:px-6 sm:pb-5">
                  <span className="text-[14px] font-medium">{stage.name}</span>
                  <span className="t-mono text-paper/80">{stage.capacity_note || `${stage.capacity} plassar`}</span>
                </figcaption>
              )}
            </figure>
            {/* Right: one tall photo with the three facts set on top of it – no empty box, no second card. */}
            <div className="img-hover relative min-h-[380px] overflow-hidden rounded-[12px] bg-ink text-paper sm:min-h-[420px] lg:col-span-4 lg:min-h-0" data-testid="arranger-key-facts">
              {sideImg ? <Img src={sideImg} alt="Solist på scena i King Oscar Amfi" width={800} sizes={SIZES.third} lazy={false} className="absolute inset-0 h-full w-full object-cover" /> : <Skeleton className="absolute inset-0 rounded-none" />}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/45 to-ink/10" aria-hidden="true" />
              {sideImg && <PhotoNumberTag src={sideImg} interactive className="absolute right-2.5 top-2.5 z-10" testId="arranger-side-photo-number" />}
              <div className="relative flex h-full flex-col justify-end p-5 sm:p-6">
                <p className="t-mono text-paper/60">Det viktigaste</p>
                <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-paper/20 pt-4">
                  <div><dt className="sr-only">Plassar i amfiet</dt><dd className="font-display text-[30px] font-semibold leading-none tracking-[-0.03em] tabular sm:text-[34px]">{stage?.capacity || "–"}</dd><dd className="t-mono-sm mt-2 text-paper/65">Plassar i amfiet</dd></div>
                  <div><dt className="sr-only">Pengar via huset</dt><dd className="font-display text-[30px] font-semibold leading-none tracking-[-0.03em] tabular sm:text-[34px]">0 kr</dd><dd className="t-mono-sm mt-2 text-paper/65">Går via huset</dd></div>
                  <div><dt className="sr-only">Betalingsfrist</dt><dd className="font-display text-[30px] font-semibold leading-none tracking-[-0.03em] tabular sm:text-[34px]">{info?.hold_hours ? `${info.hold_hours} t` : "–"}</dd><dd className="t-mono-sm mt-2 text-paper/65">Betalingsfrist</dd></div>
                </dl>
                <p className="mt-5 max-w-sm text-[13px] leading-relaxed text-paper/75">Publikum betaler rett til deg. Huset fakturerer berre ei lita avgift av stadfesta sal – i etterkant.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 border-t border-border pt-5 lg:flex-row lg:items-center lg:justify-between">
            <AnchorPills items={ANCHORS} testPrefix="arranger-anchor" />
            <div className="flex flex-col gap-1 text-[14px] text-muted-foreground sm:flex-row sm:gap-6" data-testid="arranger-wayfinding">
              <span>Berre leige eit rom? <Link to="/leig-rom" className="u-link font-medium text-foreground" data-testid="arranger-to-leig-rom">Leig rom</Link></span>
              <span>Oppleve noko? <Link to="/arrangement" className="u-link font-medium text-foreground" data-testid="arranger-to-program">Sjå programmet</Link></span>
              <span>Arrangerer du alt her? <Link to="/arrangor" className="u-link font-medium text-foreground" data-testid="arranger-to-portal">Logg inn på arrangørsida</Link></span>
            </div>
          </div>
        </Container>
      </section>

      {/* 01 FORMATS ------------------------------------------------- */}
      <Section id="format" num="01" eyebrow="Vel utgangspunkt" title={<>Kva vil du <em className="t-swash">lage</em>?</>} subtitle="Vel det som liknar mest, så fyller vi ut rom og type for deg. Alt kan endrast undervegs." tight>
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-5" data-testid="arranger-formats">
          {FORMATS.map((f, i) => <FormatTile key={f.id} f={f} rooms={rooms} index={i} />)}
        </div>
      </Section>

      {/* 02 HOW IT WORKS ------------------------------------------- */}
      <Section id="slik" num="02" eyebrow="Slik fungerer det" title={<>Frå idé til fullt hus på <em className="t-swash">fire steg</em></>} tight>
        <ol className="grid grid-cols-1 gap-10 border-t border-border pt-10 md:grid-cols-4 md:gap-8" data-testid="arranger-steps">
          {STEPS.map(([t, d], i) => (
            <FadeUp key={t} delay={i * 0.08}>
              <li className="flex gap-5 md:block">
                <span className="block font-display text-[44px] font-semibold leading-[0.8] tracking-[-0.04em] text-foreground/25 tabular sm:text-[56px]">0{i + 1}</span>
                <div className="md:mt-10">
                  <h3 className="t-h3">{t}</h3>
                  <p className="mt-2.5 max-w-sm text-[15px] leading-relaxed text-muted-foreground">{d}</p>
                </div>
              </li>
            </FadeUp>
          ))}
        </ol>
      </Section>

      {/* 03 TICKETS – the product itself, in the dark -------------- */}
      <Section id="billettar" num="03" dark eyebrow="Slik verkar billettane" title={<>Frå reservasjon til «slepp inn» – på under eit <em className="t-swash">minutt</em>.</>} subtitle="Dette er ikkje ei skisse. Det er den faktiske løysinga, slik publikum ser ho på mobilen – og slik du ser ho i døra. QR-koden er billetten; du treng korkje skrivar, lister eller passord.">
        <FadeUp><TicketStory /></FadeUp>
        <div className="mt-16 grid gap-8 border-t border-paper/15 pt-10 lg:mt-20 lg:grid-cols-12 lg:gap-10" data-testid="arranger-included">
          <div className="lg:col-span-4">
            <p className="t-mono text-paper/55">Alt dette er med</p>
            <h3 className="t-h3 mt-3 text-paper">Ingen app å installere. Ingen abonnement.</h3>
            <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-paper/65">Løysinga ligg i nettsida til huset og er klar frå første arrangement. Du betaler berre avgift av det du faktisk sel.</p>
          </div>
          <ul className="grid gap-x-8 lg:col-span-8 sm:grid-cols-2">
            {INCLUDED.map((x) => (
              <li key={x} className="flex items-start gap-3.5 border-b border-paper/10 py-3.5 text-[14.5px] leading-snug text-paper/90">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sol text-ink"><Check className="h-3 w-3" strokeWidth={3} /></span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* 04 COST + CALCULATOR -------------------------------------- */}
      <Section id="pris" num="04" eyebrow="Kva kostar det" title={<>Romleige etter prislista. Billettavgift berre når du <em className="t-swash">sel</em>.</>} tight>
        <div className="grid gap-12 border-t border-border pt-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <h3 className="t-h3">Romleige</h3>
            <p className="mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground">Rom og scene leiger du etter den vanlege prislista – prisen avheng av kva du skal bruke rommet til. Lag og foreiningar som brukar huset fast har eigne satsar. Heile huset vert avtalt direkte med styret.</p>
            <div className="mt-5"><ArrowLink to="/prisar" data-testid="arranger-prices-link">Sjå prislista</ArrowLink></div>
          </div>
          <div className="lg:col-span-7">
            <h3 className="t-h3">Billettavgift</h3>
            {isLoading || !info ? <Skeleton className="mt-4 h-40 rounded-[12px]" /> : (
              <ul className="mt-4 border-t border-border" data-testid="arranger-fees">
                {info.fee_tiers.map((t) => <FeeRow key={t.category} tier={t} />)}
              </ul>
            )}
            <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">Avgifta vert rekna av stadfesta billettsal og fakturert i etterkant. Kulturhuset tek aldri imot billettpengane.</p>
          </div>
        </div>

        <div className="mt-16 border-t border-border pt-10">
          <div className="max-w-2xl">
            <Eyebrow>Rekn ut</Eyebrow>
            <h3 className="t-h2 mt-4 text-balance">Kva sit <em className="t-swash">du</em> att med?</h3>
            <p className="t-lead mt-4 text-muted-foreground text-pretty">Dra i glidarane. Tala er dei same som på fakturaen – ingen skjulte tillegg.</p>
          </div>
          {info ? <FeeCalculator tiers={info.fee_tiers} vatPercent={info.vat_percent} maxAudience={stage?.capacity || 187} className="mt-10" /> : <Skeleton className="mt-10 h-72 rounded-[12px]" />}
        </div>
      </Section>

      {/* 05 FREE DATES --------------------------------------------- */}
      <Section id="datoar" num="05" eyebrow="Ledige datoar" title={<>Neste ledige helgekveldar i <em className="t-swash">amfiet</em></>} subtitle="Henta rett frå kalenderen, akkurat no. Trykk på ein dato, så er rom og dato alt fylt ut i førespurnaden." tight>
        <FreeDates roomId="king-oscar-amfi" roomName="amfiet" />
      </Section>

      {/* 06 ALREADY HAPPENING -------------------------------------- */}
      {events?.length > 0 && (
        <Section num="06" eyebrow="Dette skjer alt her" title={<>Du er i godt <em className="t-swash">selskap</em></>} action={<ArrowLink to="/arrangement" data-testid="arranger-all-events-link">Sjå heile programmet</ArrowLink>} tight>
          <div className="divide-y divide-border border-t border-border" data-testid="arranger-events">{events.map((e) => <EventRow key={e.id} event={e} />)}</div>
        </Section>
      )}

      {/* 07 FAQ + THE DEAL ----------------------------------------- */}
      <Section id="sporsmal" num="07" eyebrow="Spørsmål og svar" title={<>Det folk <em className="t-swash">lurer</em> på</>} tight>
        <div className="grid gap-10 border-t border-border pt-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <p className="t-mono text-muted-foreground">Avtalen i klartekst</p>
            <ul className="mt-4 space-y-3 text-[14px] leading-relaxed text-muted-foreground" data-testid="arranger-terms">
              <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Billetten er ein avtale mellom deg og publikum. Kulturhuset stiller berre løysinga til rådvelde.</li>
              <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Du får pengane direkte. Huset fakturerer avgift av stadfesta sal i etterkant.</li>
              <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />{info?.refund_text || "Refusjon ved avlysing er opp til arrangøren."}</li>
              <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Namn og kontaktinfo på publikum er berre synleg for deg og styret, og vert brukt berre til arrangementet.</li>
            </ul>
            <p className="mt-6 text-[14px] text-muted-foreground">Finn du ikkje svaret? <Link to="/kontakt" className="u-link font-medium text-foreground">Ta kontakt</Link>.</p>
          </div>
          <Accordion type="single" collapsible className="lg:col-span-8" data-testid="arranger-faq">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} className={cn(i === FAQ.length - 1 && "border-b-0")}>
                <AccordionTrigger className="py-5 text-left text-[17px] font-medium hover:no-underline" data-testid={`arranger-faq-${i}`}>{q}</AccordionTrigger>
                <AccordionContent className="max-w-2xl text-[15px] leading-relaxed text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CTA + LINK AGAIN ------------------------------------------ */}
      <CtaBand eyebrow="Klar?" title={<>Fortel oss kva du vil lage, så finn vi ein dato.</>} text="Førespurnaden er ikkje bindande. Styret svarar på e-post – og hjelper gjerne til med å finne riktig rom og format.">
        <Button asChild variant="light" size="lg" data-testid="arranger-cta-band-button"><Link to="/arranger/soknad">Send arrangørførespurnad <ArrowUpRight className="h-4 w-4" /></Link></Button>
        <Button asChild variant="lightOutline" size="lg" data-testid="arranger-cta-contact"><Link to="/kontakt">Snakk med oss først</Link></Button>
        <div className="mt-6 w-full border-t border-paper/15 pt-8 lg:mt-8" id="lenke">
          <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-5">
              <p className="t-mono text-paper/55">Allereie arrangør hos oss?</p>
              <h3 className="t-h3 mt-3 text-paper">Få arrangørlenka på nytt</h3>
              <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-paper/70">Skriv e-posten du er registrert med, så sender vi den personlege lenka di på nytt{site?.contact_email ? ` – eller spør på ${site.contact_email}` : ""}.</p>
            </div>
            <div className="lg:col-span-7 lg:pt-9"><OrganizerLinkRequest light /></div>
          </div>
        </div>
      </CtaBand>

      {/* Mobile sticky CTA ---------------------------------------- */}
      <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4 lg:hidden" data-testid="arranger-mobile-bar">
        <div className="flex items-center justify-between gap-3 rounded-full border border-border/70 bg-background/85 py-2 pl-5 pr-2 shadow-lift backdrop-blur-xl">
          <div className="min-w-0"><p className="truncate text-[14px] font-medium">Arranger noko</p><p className="truncate text-xs text-muted-foreground">Ikkje bindande · svar på e-post</p></div>
          <Button asChild size="lg" className="rounded-full" data-testid="arranger-mobile-cta"><Link to="/arranger/soknad">Send førespurnad <ArrowRight className="h-4 w-4" /></Link></Button>
        </div>
      </div>
    </div>
  );
}
